/**
 * Error Toast Notification Component
 * User-Friendly Error Display System
 *
 * @description Issue #283 対応 - エラートースト通知システム
 * @backend NestJS ErrorResponse統合
 * @features アニメーション・自動消去・リトライ・複数通知管理
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useErrorContext, type ErrorNotification } from '../contexts/ErrorContext';
import { ErrorSeverity } from '../utils/error';

// ====================================
// Toast Component Props
// ====================================

interface ErrorToastProps {
  notification: ErrorNotification;
  onDismiss: (id: string) => void;
  onRetry?: (id: string) => void;
}

// ====================================
// Individual Toast Component
// ====================================

const ErrorToast: React.FC<ErrorToastProps> = ({
  notification,
  onDismiss,
  onRetry,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const { error, id, autoClose, duration } = notification;

  // Animate in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300); // Match CSS animation duration
  }, [id, onDismiss]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!autoClose || duration <= 0) return;

    const timer = setTimeout(() => {
      handleDismiss();
    }, duration);

    return () => clearTimeout(timer);
  }, [autoClose, duration, handleDismiss]);

  const handleRetry = useCallback(() => {
    if (onRetry && error.isRetryable) {
      onRetry(id);
    }
  }, [id, onRetry, error.isRetryable]);

  const getSeverityIcon = () => {
    switch (error.severity) {
      case ErrorSeverity.CRITICAL: return '🚨';
      case ErrorSeverity.HIGH: return '⚠️';
      case ErrorSeverity.MEDIUM: return '❗';
      case ErrorSeverity.LOW: return 'ℹ️';
      default: return '❗';
    }
  };

  const getSeverityColor = () => {
    switch (error.severity) {
      case ErrorSeverity.CRITICAL: return '#dc3545'; // Red
      case ErrorSeverity.HIGH: return '#fd7e14'; // Orange
      case ErrorSeverity.MEDIUM: return '#ffc107'; // Yellow
      case ErrorSeverity.LOW: return '#17a2b8'; // Blue
      default: return '#6c757d'; // Gray
    }
  };

  const suggestions = error.getSuggestions();
  const showSuggestions = suggestions.length > 0 && error.severity !== ErrorSeverity.LOW;

  return (
    <div
      className={`error-toast error-toast--${error.severity} ${
        isVisible ? 'error-toast--visible' : ''
      } ${
        isExiting ? 'error-toast--exiting' : ''
      }`}
      style={{
        '--severity-color': getSeverityColor(),
      } as React.CSSProperties}
    >
      <div className="error-toast__header">
        <div className="error-toast__icon">
          {getSeverityIcon()}
        </div>
        <div className="error-toast__title">
          {error.severity === ErrorSeverity.CRITICAL && 'システムエラー'}
          {error.severity === ErrorSeverity.HIGH && 'エラーが発生しました'}
          {error.severity === ErrorSeverity.MEDIUM && 'エラーが発生しました'}
          {error.severity === ErrorSeverity.LOW && 'お知らせ'}
        </div>
        <button
          className="error-toast__close"
          onClick={handleDismiss}
          aria-label="通知を閉じる"
        >
          ×
        </button>
      </div>

      <div className="error-toast__content">
        <p className="error-toast__message">
          {error.userMessage}
        </p>

        {showSuggestions && (
          <div className="error-toast__suggestions">
            <details className="error-toast__suggestions-details">
              <summary>解決方法を見る</summary>
              <ul className="error-toast__suggestions-list">
                {suggestions.slice(0, 2).map((suggestion, index) => (
                  <li key={index} className="error-toast__suggestion">
                    {suggestion}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </div>

      <div className="error-toast__actions">
        {error.isRetryable && onRetry && (
          <button
            className="error-toast__action error-toast__action--retry"
            onClick={handleRetry}
          >
            再試行
          </button>
        )}
        <button
          className="error-toast__action error-toast__action--dismiss"
          onClick={handleDismiss}
        >
          閉じる
        </button>
      </div>

      {/* Progress bar for auto-dismiss */}
      {autoClose && duration > 0 && (
        <div
          className="error-toast__progress"
          style={{
            animationDuration: `${duration}ms`,
          }}
        />
      )}

      {/* CSS Styles */}
      <style>{`
        .error-toast {
          position: relative;
          width: 380px;
          max-width: 90vw;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border-left: 4px solid var(--severity-color);
          margin-bottom: 12px;
          overflow: hidden;
          transform: translateX(100%);
          opacity: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .error-toast--visible {
          transform: translateX(0);
          opacity: 1;
        }

        .error-toast--exiting {
          transform: translateX(100%);
          opacity: 0;
        }

        .error-toast--critical {
          border-left-color: #dc3545;
          background: linear-gradient(135deg, #fff 0%, #fff5f5 100%);
        }

        .error-toast--high {
          border-left-color: #fd7e14;
          background: linear-gradient(135deg, #fff 0%, #fff8f0 100%);
        }

        .error-toast--medium {
          border-left-color: #ffc107;
          background: linear-gradient(135deg, #fff 0%, #fffdf0 100%);
        }

        .error-toast--low {
          border-left-color: #17a2b8;
          background: linear-gradient(135deg, #fff 0%, #f0faff 100%);
        }

        .error-toast__header {
          display: flex;
          align-items: center;
          padding: 12px 16px 8px 16px;
        }

        .error-toast__icon {
          font-size: 18px;
          margin-right: 8px;
          flex-shrink: 0;
        }

        .error-toast__title {
          flex: 1;
          font-weight: 600;
          font-size: 14px;
          color: #2d3748;
          margin: 0;
        }

        .error-toast__close {
          background: none;
          border: none;
          font-size: 18px;
          color: #a0aec0;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .error-toast__close:hover {
          background: #f7fafc;
          color: #4a5568;
        }

        .error-toast__content {
          padding: 0 16px 12px 16px;
        }

        .error-toast__message {
          font-size: 13px;
          line-height: 1.4;
          color: #4a5568;
          margin: 0 0 8px 0;
        }

        .error-toast__suggestions {
          margin-top: 8px;
        }

        .error-toast__suggestions-details {
          font-size: 12px;
        }

        .error-toast__suggestions-details summary {
          color: var(--severity-color);
          cursor: pointer;
          font-weight: 500;
          outline: none;
          user-select: none;
        }

        .error-toast__suggestions-details summary:hover {
          text-decoration: underline;
        }

        .error-toast__suggestions-list {
          margin: 6px 0 0 0;
          padding-left: 16px;
          list-style: none;
        }

        .error-toast__suggestions-list::before {
          content: '';
          position: absolute;
          margin-left: -12px;
          margin-top: 6px;
          width: 2px;
          height: calc(100% - 12px);
          background: var(--severity-color);
          opacity: 0.3;
        }

        .error-toast__suggestion {
          margin: 4px 0;
          padding-left: 8px;
          font-size: 12px;
          color: #718096;
          position: relative;
        }

        .error-toast__suggestion::before {
          content: '•';
          position: absolute;
          left: -4px;
          color: var(--severity-color);
        }

        .error-toast__actions {
          display: flex;
          gap: 8px;
          padding: 0 16px 12px 16px;
          justify-content: flex-end;
        }

        .error-toast__action {
          padding: 6px 12px;
          border: 1px solid transparent;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .error-toast__action--retry {
          color: var(--severity-color);
          border-color: var(--severity-color);
        }

        .error-toast__action--retry:hover {
          background: var(--severity-color);
          color: white;
        }

        .error-toast__action--dismiss {
          color: #718096;
          border-color: #e2e8f0;
        }

        .error-toast__action--dismiss:hover {
          background: #f7fafc;
          color: #4a5568;
        }

        .error-toast__progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 2px;
          background: var(--severity-color);
          opacity: 0.6;
          animation: progress-shrink linear;
          transform-origin: left;
        }

        @keyframes progress-shrink {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }

        /* Responsive design */
        @media (max-width: 640px) {
          .error-toast {
            width: 90vw;
            margin-left: 5vw;
            margin-right: 5vw;
          }

          .error-toast__suggestions-details[open] .error-toast__suggestions-list {
            max-height: 60px;
            overflow-y: auto;
          }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .error-toast {
            background: #2d3748;
            color: #e2e8f0;
          }

          .error-toast__title {
            color: #f7fafc;
          }

          .error-toast__message {
            color: #cbd5e0;
          }

          .error-toast__suggestion {
            color: #a0aec0;
          }

          .error-toast__close:hover {
            background: #4a5568;
            color: #e2e8f0;
          }

          .error-toast__action {
            background: #2d3748;
            border-color: #4a5568;
          }

          .error-toast__action--dismiss:hover {
            background: #4a5568;
            color: #e2e8f0;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          .error-toast {
            transition: none;
          }

          .error-toast__progress {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

// ====================================
// Toast Container Component
// ====================================

export const ErrorToastContainer: React.FC = () => {
  const { errors, enableToasts, dismissError, retryError } = useErrorContext();

  // Filter visible errors
  const visibleErrors = errors.filter(error => error.isVisible && enableToasts);

  if (visibleErrors.length === 0) {
    return null;
  }

  return (
    <div className="error-toast-container">
      {visibleErrors.map(notification => (
        <ErrorToast
          key={notification.id}
          notification={notification}
          onDismiss={dismissError}
          onRetry={notification.onRetry ? retryError : undefined}
        />
      ))}

      <style>{`
        .error-toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10000;
          pointer-events: none;
        }

        .error-toast-container > :global(.error-toast) {
          pointer-events: auto;
        }

        /* Mobile positioning */
        @media (max-width: 640px) {
          .error-toast-container {
            top: 10px;
            right: 0;
            left: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};

// ====================================
// Specialized Toast Hooks
// ====================================

/**
 * Hook for showing success notifications
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useSuccessToast = () => {
  const { reportError } = useErrorContext();

  return useCallback((message: string, duration = 3000) => {
    // Create a low-severity "error" for success messages
    const successError = {
      statusCode: 200,
      error: 'SUCCESS',
      message,
      timestamp: new Date().toISOString(),
      path: '',
      details: {
        isSuccess: true,
        customDuration: duration,
      },
    };

    reportError(successError, { type: 'success_notification' });
  }, [reportError]);
};

/**
 * Hook for showing warning notifications
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useWarningToast = () => {
  const { reportError } = useErrorContext();

  return useCallback((message: string, suggestions?: string[]) => {
    const warningError = {
      statusCode: 200,
      error: 'WARNING',
      message,
      timestamp: new Date().toISOString(),
      path: '',
      details: {
        isWarning: true,
        suggestions,
      },
    };

    reportError(warningError, { type: 'warning_notification' });
  }, [reportError]);
};

export default ErrorToast;